import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { isBefore, parseISO, formatDistanceToNow } from "date-fns";

import { MOCK_USERS, generateMockTasks, MOCK_PROJECTS } from "./utils";
import {
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  Tooltip,
  Divider,
  InputAdornment,
  AvatarGroup,
  Avatar,
  Drawer,
  Slider,
} from "@mui/material";
import {
  indigo,
  green,
  amber,
  red,
  blue,
  purple,
  grey,
} from "@mui/material/colors";
import {
  LayoutDashboard,
  Kanban,
  BarChart3,
  Plus,
  Search,
  Filter,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle2,
  User as UserIcon,
  X,
  CheckSquare,
  Paperclip,
  MessageSquare,
  Trash2,
  Upload,
  FileText,
  Send,
  ArrowLeft,
  FolderKanban,
} from "lucide-react";

import { Card } from "./components/UI";
import ProjectCard from "./components/ProjectCard";
import { TaskCard } from "./components/TaskCard";
import KanbanColumn from "./components/KanbanColumn";
import GanttChart from "./components/GanttChart";
import AnalyticsDashboard from "./components/AnalyticsDashboard";

// Simulate currently logged in user
const CURRENT_USER = MOCK_USERS[0];

const Progetti = () => {
  // State
  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);

  const [tasks, setTasks] = useState([]);
  const [viewMode, setViewMode] = useState("board");
  const [filter, setFilter] = useState({
    search: "",
    assigneeId: "all",
    priority: "all",
    status: "all",
    tag: "all",
  });
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  const [isNewProjectModalOpen, setIsNewProjectModalOpen] = useState(false);
  const [activeDragTaskId, setActiveDragTaskId] = useState(null);

  // New states for input fields in drawer
  const [newChecklistText, setNewChecklistText] = useState("");
  const [newCommentText, setNewCommentText] = useState("");
  const fileInputRef = useRef(null);

  // Column Management States
  const [isRenameColumnModalOpen, setIsRenameColumnModalOpen] = useState(false);
  const [columnToRename, setColumnToRename] = useState(null);
  const [newColumnName, setNewColumnName] = useState("");
  const [isAddColumnModalOpen, setIsAddColumnModalOpen] = useState(false);
  const [newStatusName, setNewStatusName] = useState("");

  // Sensors for DnD
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Load Data
  useEffect(() => {
    // Load Projects
    const savedProjects = localStorage.getItem("taskflow_projects");
    if (savedProjects) {
      setProjects(JSON.parse(savedProjects));
    } else {
      setProjects(MOCK_PROJECTS);
    }

    // Load Tasks
    const savedTasks = localStorage.getItem("taskflow_data");
    if (savedTasks) {
      setTasks(JSON.parse(savedTasks));
    } else {
      setTasks(generateMockTasks());
    }
  }, []);

  // Persist Data
  useEffect(() => {
    if (tasks.length > 0)
      localStorage.setItem("taskflow_data", JSON.stringify(tasks));
    if (projects.length > 0)
      localStorage.setItem("taskflow_projects", JSON.stringify(projects));
  }, [tasks, projects]);

  // Derived State
  const activeProject = useMemo(
    () => projects.find((p) => p.id === activeProjectId),
    [projects, activeProjectId]
  );

  // Filtering Logic
  const filteredTasks = useMemo(() => {
    if (!activeProjectId) return []; // Only show tasks when a project is active

    return tasks.filter((t) => {
      // Project Filter
      if (t.projectId !== activeProjectId) return false;

      // Other Filters
      const matchesSearch =
        t.title.toLowerCase().includes(filter.search.toLowerCase()) ||
        t.description.toLowerCase().includes(filter.search.toLowerCase());
      const matchesAssignee =
        filter.assigneeId === "all" ||
        t.assigneeIds.includes(filter.assigneeId);
      const matchesPriority =
        filter.priority === "all" || t.priority === filter.priority;
      const matchesStatus =
        filter.status === "all" || t.status === filter.status;
      const matchesTag = filter.tag === "all" || t.tags.includes(filter.tag);
      return (
        matchesSearch &&
        matchesAssignee &&
        matchesPriority &&
        matchesStatus &&
        matchesTag
      );
    });
  }, [tasks, filter, activeProjectId]);

  // KPIs
  const kpis = useMemo(() => {
    const projectTasks = activeProjectId
      ? tasks.filter((t) => t.projectId === activeProjectId)
      : [];
    const total = projectTasks.length;
    const done = projectTasks.filter((t) => t.status === "Done").length;
    const inProgress = projectTasks.filter(
      (t) => t.status === "In Progress"
    ).length;
    const overdue = projectTasks.filter(
      (t) => isBefore(parseISO(t.dueDate), new Date()) && t.status !== "Done"
    ).length;
    return { total, done, inProgress, overdue };
  }, [tasks, activeProjectId]);

  // DnD Handlers
  const handleDragStart = (event) => {
    setActiveDragTaskId(event.active.id);
  };

  const handleDragEnd = (event) => {
    setActiveDragTaskId(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    const activeTask = tasks.find((t) => t.id === activeId);
    if (!activeTask) return;

    const statuses = activeProject?.columns || [
      "Backlog",
      "To Do",
      "In Progress",
      "Review",
      "Done",
    ];
    if (statuses.includes(overId)) {
      if (activeTask.status !== overId) {
        setTasks((prev) =>
          prev.map((t) => (t.id === activeId ? { ...t, status: overId } : t))
        );
      }
      return;
    }

    const overTask = tasks.find((t) => t.id === overId);
    if (overTask) {
      if (activeTask.status !== overTask.status) {
        setTasks((prev) =>
          prev.map((t) =>
            t.id === activeId ? { ...t, status: overTask.status } : t
          )
        );
      } else {
        const oldIndex = tasks.findIndex((t) => t.id === activeId);
        const newIndex = tasks.findIndex((t) => t.id === overId);
        setTasks((prev) => arrayMove(prev, oldIndex, newIndex));
      }
    }
  };

  // CRUD Operations
  const handleCreateProject = (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const columnsInput = formData.get("columns");
    const columns = columnsInput
      ? columnsInput
          .split(",")
          .map((c) => c.trim())
          .filter((c) => c !== "")
      : ["Backlog", "To Do", "In Progress", "Review", "Done"];

    const newProject = {
      id: `p-${Date.now()}`,
      name: formData.get("name"),
      description: formData.get("description"),
      key: formData.get("key").toUpperCase(),
      status: "Active",
      createdAt: new Date().toISOString(),
      color: ["bg-indigo-600", "bg-green-600", "bg-amber-600", "bg-rose-600"][
        Math.floor(Math.random() * 4)
      ],
      columns: columns,
    };
    setProjects([...projects, newProject]);
    setIsNewProjectModalOpen(false);
  };

  const handleRenameColumn = () => {
    if (!activeProjectId || !columnToRename || !newColumnName.trim()) return;

    setProjects((prev) =>
      prev.map((p) => {
        if (p.id === activeProjectId) {
          return {
            ...p,
            columns: p.columns.map((c) =>
              c === columnToRename ? newColumnName.trim() : c
            ),
          };
        }
        return p;
      })
    );

    // Also update tasks status
    setTasks((prev) =>
      prev.map((t) => {
        if (t.projectId === activeProjectId && t.status === columnToRename) {
          return { ...t, status: newColumnName.trim() };
        }
        return t;
      })
    );

    setIsRenameColumnModalOpen(false);
    setColumnToRename(null);
    setNewColumnName("");
  };

  const handleAddColumn = (e) => {
    e.preventDefault();
    if (!activeProjectId || !newStatusName.trim()) return;

    setProjects((prev) =>
      prev.map((p) => {
        if (p.id === activeProjectId) {
          if (p.columns.includes(newStatusName.trim())) return p;
          return {
            ...p,
            columns: [...p.columns, newStatusName.trim()],
          };
        }
        return p;
      })
    );

    setIsAddColumnModalOpen(false);
    setNewStatusName("");
  };

  const handleCreateTask = (e) => {
    e.preventDefault();
    if (!activeProjectId) return;

    const formData = new FormData(e.currentTarget);
    const assignees = formData.getAll("assignee");
    const title = formData.get("title");

    // Auto-prefix title with Project Key if not present
    const projectKey = activeProject?.key || "TASK";
    const finalTitle = title.startsWith(projectKey)
      ? title
      : `${projectKey}-${Math.floor(Math.random() * 1000)}: ${title}`;

    const newTask = {
      id: `t-${Date.now()}`,
      projectId: activeProjectId,
      title: finalTitle,
      description: formData.get("description"),
      status:
        formData.get("status") || activeProject?.columns?.[0] || "Backlog",
      priority: formData.get("priority"),
      assigneeIds: assignees,
      startDate: formData.get("startDate"),
      dueDate: formData.get("dueDate"),
      estimateHours: Number(formData.get("estimate")),
      tags: ["Feature"],
      progress: 0,
      checklist: [],
      attachments: [],
      comments: [],
      activityLog: [
        {
          id: `l-${Date.now()}`,
          text: "Task created",
          createdAt: new Date().toISOString(),
        },
      ],
    };
    setTasks([...tasks, newTask]);
    setIsNewTaskModalOpen(false);
  };

  // Task Detail Logic
  const updateSelectedTask = (updates) => {
    if (!selectedTask) return;
    const updated = { ...selectedTask, ...updates };
    setSelectedTask(updated);
    setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
  };

  const toggleTaskAssignee = (userId) => {
    if (!selectedTask) return;
    const currentIds = selectedTask.assigneeIds || [];
    const newIds = currentIds.includes(userId)
      ? currentIds.filter((id) => id !== userId)
      : [...currentIds, userId];
    updateSelectedTask({ assigneeIds: newIds });
  };

  // Feature Handlers (Checklist, Comments, etc.)
  const handleAddChecklist = () => {
    if (!selectedTask || !newChecklistText.trim()) return;
    const newItem = {
      id: `cl-${Date.now()}`,
      text: newChecklistText,
      isCompleted: false,
    };
    updateSelectedTask({
      checklist: [...(selectedTask.checklist || []), newItem],
    });
    setNewChecklistText("");
  };

  const toggleChecklistItem = (itemId) => {
    if (!selectedTask) return;
    const newChecklist = selectedTask.checklist.map((item) =>
      item.id === itemId ? { ...item, isCompleted: !item.isCompleted } : item
    );
    updateSelectedTask({ checklist: newChecklist });
  };

  const assignChecklistItem = (itemId, userId) => {
    if (!selectedTask) return;
    const newChecklist = selectedTask.checklist.map((item) =>
      item.id === itemId ? { ...item, assigneeId: userId } : item
    );
    updateSelectedTask({ checklist: newChecklist });
  };

  const deleteChecklistItem = (itemId) => {
    if (!selectedTask) return;
    updateSelectedTask({
      checklist: selectedTask.checklist.filter((item) => item.id !== itemId),
    });
  };

  const handleAddComment = () => {
    if (!selectedTask || !newCommentText.trim()) return;
    const newComment = {
      id: `cm-${Date.now()}`,
      userId: CURRENT_USER.id,
      text: newCommentText,
      createdAt: new Date().toISOString(),
    };
    updateSelectedTask({
      comments: [...(selectedTask.comments || []), newComment],
    });
    setNewCommentText("");
  };

  const handleFileUpload = (e) => {
    if (!selectedTask || !e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const newAttachment = {
      id: `att-${Date.now()}`,
      name: file.name,
      url: "#",
      type: file.name.split(".").pop() || "file",
      size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      uploadedAt: new Date().toISOString(),
    };
    updateSelectedTask({
      attachments: [...(selectedTask.attachments || []), newAttachment],
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // --- RENDER ---

  if (!activeProjectId) {
    // PROJECT LIST VIEW
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
          color: "text.primary",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            position: "sticky",
            top: 0,
            zIndex: 30,
          }}
        >
          <Box maxWidth="lg" mx="auto" px={{ xs: 2, sm: 3, lg: 4 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              height={64}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    bgcolor: "primary.main",
                    p: 1,
                    borderRadius: 2,
                    display: "flex",
                  }}
                >
                  <LayoutDashboard size={24} color="white" />
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  TaskFlow Pro
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Plus size={18} />}
                onClick={() => setIsNewProjectModalOpen(true)}
              >
                New Project
              </Button>
            </Box>
          </Box>
        </Paper>

        <Box
          component="main"
          flex={1}
          maxWidth="lg"
          width="100%"
          mx="auto"
          px={{ xs: 2, sm: 3, lg: 4 }}
          py={6}
        >
          <Typography variant="h5" fontWeight="bold" mb={4}>
            Your Projects
          </Typography>
          <Grid container spacing={4}>
            {projects.map((project) => (
              <Grid item xs={12} md={6} lg={4} key={project.id}>
                <ProjectCard
                  project={project}
                  taskCount={
                    tasks.filter((t) => t.projectId === project.id).length
                  }
                  onClick={() => setActiveProjectId(project.id)}
                />
              </Grid>
            ))}
            {projects.length === 0 && (
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    py: 10,
                    textAlign: "center",
                    borderStyle: "dashed",
                    bgcolor: "background.paper",
                  }}
                >
                  <FolderKanban
                    size={48}
                    color={grey[400]}
                    style={{ margin: "0 auto", marginBottom: 16 }}
                  />
                  <Typography color="text.secondary">
                    No projects yet. Create one to get started.
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* New Project Modal */}
        <Dialog
          open={isNewProjectModalOpen}
          onClose={() => setIsNewProjectModalOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <form onSubmit={handleCreateProject}>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={3} pt={1}>
                <TextField
                  label="Project Name"
                  name="name"
                  required
                  fullWidth
                  placeholder="e.g. Website Redesign"
                />
                <TextField
                  label="Key (Prefix)"
                  name="key"
                  required
                  fullWidth
                  inputProps={{
                    maxLength: 4,
                    style: { textTransform: "uppercase" },
                  }}
                  placeholder="e.g. WEB"
                />
                <TextField
                  label="Description"
                  name="description"
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Brief details..."
                />
                <TextField
                  label="Status Columns (comma separated)"
                  name="columns"
                  fullWidth
                  placeholder="e.g. Backlog, To Do, In Progress, Review, Done"
                  helperText="Leave empty for default columns"
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button
                onClick={() => setIsNewProjectModalOpen(false)}
                color="inherit"
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained">
                Create Project
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
    );
  }

  // ACTIVE PROJECT VIEW
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "sticky",
          top: 0,
          zIndex: 30,
        }}
      >
        <Box maxWidth="xl" mx="auto" px={{ xs: 2, sm: 3, lg: 4 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            height={64}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton
                onClick={() => setActiveProjectId(null)}
                size="small"
                title="Back to Projects"
              >
                <ArrowLeft size={20} />
              </IconButton>
              <Divider
                orientation="vertical"
                flexItem
                variant="middle"
                sx={{ height: 24, alignSelf: "center", mx: 1 }}
              />
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 2,
                    bgcolor: (theme) => {
                      const c = activeProject?.color || "";
                      if (c.includes("green")) return green[600];
                      if (c.includes("amber")) return amber[600];
                      if (c.includes("rose")) return red[600];
                      return indigo[600];
                    },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "0.75rem",
                  }}
                >
                  {activeProject?.key}
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
                    {activeProject?.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    Project Dashboard
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box display={{ xs: "none", md: "block" }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, val) => {
                  if (val) setViewMode(val);
                }}
                size="small"
                sx={{ bgcolor: "background.paper" }}
              >
                {[
                  { id: "board", icon: Kanban, label: "Board" },
                  { id: "gantt", icon: Calendar, label: "Gantt" },
                  { id: "analytics", icon: BarChart3, label: "Analytics" },
                ].map((view) => (
                  <ToggleButton
                    key={view.id}
                    value={view.id}
                    sx={{ px: 2, gap: 1 }}
                  >
                    <view.icon size={16} />
                    {view.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => setIsNewTaskModalOpen(true)}
              sx={{ borderRadius: 2 }}
            >
              New Task
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Main Content */}
      {/* Main Content */}
      <Box
        component="main"
        flex={1}
        maxWidth="xl"
        width="100%"
        mx="auto"
        px={{ xs: 2, sm: 3, lg: 4 }}
        py={4}
      >
        {/* KPI Section */}
        <Grid container spacing={3} mb={4}>
          {[
            {
              label: "Total Tasks",
              value: kpis.total,
              icon: LayoutDashboard,
              color: blue[600],
              bg: blue[50],
            },
            {
              label: "In Progress",
              value: kpis.inProgress,
              icon: Clock,
              color: amber[600],
              bg: amber[50],
            },
            {
              label: "Completed",
              value: kpis.done,
              icon: CheckCircle2,
              color: green[600],
              bg: green[50],
            },
            {
              label: "Overdue",
              value: kpis.overdue,
              icon: AlertCircle,
              color: red[600],
              bg: red[50],
            },
          ].map((stat, i) => (
            <Grid item xs={12} sm={6} lg={3} key={i}>
              <Card
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color="text.secondary"
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    color="text.primary"
                    mt={0.5}
                  >
                    {stat.value}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    bgcolor: stat.bg,
                    display: "flex",
                    color: stat.color,
                  }}
                >
                  <stat.icon size={24} />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Filters */}
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            bgcolor: "background.paper",
            borderRadius: 2,
          }}
        >
          <Box flex={1} minWidth={200}>
            <TextField
              placeholder="Search tasks..."
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color={grey[400]} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              color="text.secondary"
            >
              <Filter size={18} />
            </Box>
            <TextField
              select
              size="small"
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="Backlog">Backlog</MenuItem>
              <MenuItem value="To Do">To Do</MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
              <MenuItem value="Review">Review</MenuItem>
              <MenuItem value="Done">Done</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              value={filter.priority}
              onChange={(e) =>
                setFilter({ ...filter, priority: e.target.value })
              }
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">All Priorities</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Critical">Critical</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              value={filter.assigneeId}
              onChange={(e) =>
                setFilter({ ...filter, assigneeId: e.target.value })
              }
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="all">All Assignees</MenuItem>
              {MOCK_USERS.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Paper>

        {/* Views */}
        {/* Views */}
        <Box minHeight={600}>
          {viewMode === "board" && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <Box
                display="flex"
                gap={2}
                height="100%"
                alignItems="start"
                sx={{ overflowX: "auto", pb: 2 }}
              >
                {(
                  activeProject?.columns || [
                    "Backlog",
                    "To Do",
                    "In Progress",
                    "Review",
                    "Done",
                  ]
                ).map((status) => (
                  <Box key={status} height="100%" sx={{ minWidth: 280 }}>
                    <KanbanColumn
                      id={status}
                      tasks={filteredTasks.filter((t) => t.status === status)}
                      onClickTask={(t) => {
                        setSelectedTask(t);
                        setIsDrawerOpen(true);
                      }}
                      onRenameColumn={(oldName) => {
                        setColumnToRename(oldName);
                        setNewColumnName(oldName);
                        setIsRenameColumnModalOpen(true);
                      }}
                    />
                  </Box>
                ))}
                {/* Plus Column Button */}
                <Box
                  sx={{
                    minWidth: 280,
                    height: 100,
                    border: "2px dashed",
                    borderColor: "divider",
                    borderRadius: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "action.hover",
                      borderColor: "primary.main",
                      color: "primary.main",
                    },
                  }}
                  onClick={() => setIsAddColumnModalOpen(true)}
                >
                  <Plus size={24} style={{ marginRight: 8 }} />
                  <Typography fontWeight="bold">Add Status</Typography>
                </Box>
              </Box>
              <DragOverlay>
                {activeDragTaskId ? (
                  <TaskCard
                    task={tasks.find((t) => t.id === activeDragTaskId)}
                    isOverlay
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          )}

          {viewMode === "gantt" && (
            <GanttChart
              tasks={filteredTasks}
              users={MOCK_USERS}
              project={activeProject}
              onTaskClick={(t) => {
                setSelectedTask(t);
                setIsDrawerOpen(true);
              }}
              onRenameColumn={(oldName) => {
                setColumnToRename(oldName);
                setNewColumnName(oldName);
                setIsRenameColumnModalOpen(true);
              }}
            />
          )}

          {viewMode === "analytics" && (
            <AnalyticsDashboard tasks={filteredTasks} users={MOCK_USERS} />
          )}
        </Box>
      </Box>

      {/* Task Drawer */}
      <Drawer
        anchor="right"
        open={isDrawerOpen && !!selectedTask}
        onClose={() => setIsDrawerOpen(false)}
        PaperProps={{ sx: { width: "100%", maxWidth: 600 } }}
      >
        {selectedTask && (
          <Box display="flex" flexDirection="column" height="100%">
            {/* Header */}
            <Box
              p={3}
              borderBottom={1}
              borderColor="divider"
              display="flex"
              justifyContent="space-between"
              alignItems="start"
            >
              <Box flex={1} mr={2}>
                <TextField
                  variant="standard"
                  fullWidth
                  value={selectedTask.title}
                  onChange={(e) =>
                    updateSelectedTask({ title: e.target.value })
                  }
                  inputProps={{
                    style: { fontSize: "1.25rem", fontWeight: "bold" },
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontFamily="monospace"
                  display="block"
                  mt={0.5}
                >
                  {selectedTask.id}
                </Typography>
              </Box>
              <IconButton onClick={() => setIsDrawerOpen(false)}>
                <X />
              </IconButton>
            </Box>

            <Box flex={1} overflow="auto" p={3}>
              <Box display="flex" flexDirection="column" gap={4}>
                {/* Meta Grid */}
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                      textTransform="uppercase"
                    >
                      Status
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={selectedTask.status}
                      onChange={(e) =>
                        updateSelectedTask({ status: e.target.value })
                      }
                      sx={{ mt: 1 }}
                    >
                      {(
                        activeProject?.columns || [
                          "Backlog",
                          "To Do",
                          "In Progress",
                          "Review",
                          "Done",
                        ]
                      ).map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                      textTransform="uppercase"
                    >
                      Priority
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={selectedTask.priority}
                      onChange={(e) =>
                        updateSelectedTask({ priority: e.target.value })
                      }
                      sx={{ mt: 1 }}
                    >
                      {["Low", "Medium", "High", "Critical"].map((p) => (
                        <MenuItem key={p} value={p}>
                          {p}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                      textTransform="uppercase"
                    >
                      Start
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      value={selectedTask.startDate.split("T")[0]}
                      sx={{ mt: 1 }}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                      textTransform="uppercase"
                    >
                      Due
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      value={selectedTask.dueDate.split("T")[0]}
                      sx={{ mt: 1 }}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>

                {/* Progress */}
                <Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                      textTransform="uppercase"
                    >
                      Progress
                    </Typography>
                    <Typography variant="caption" fontWeight="bold">
                      {selectedTask.progress}%
                    </Typography>
                  </Box>
                  <Slider
                    value={selectedTask.progress}
                    onChange={(e, val) => updateSelectedTask({ progress: val })}
                    min={0}
                    max={100}
                  />
                </Box>

                {/* Description */}
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="text.secondary"
                    textTransform="uppercase"
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={1}
                  >
                    <FileText size={16} /> Description
                  </Typography>
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    value={selectedTask.description}
                    onChange={(e) =>
                      updateSelectedTask({ description: e.target.value })
                    }
                    placeholder="Add a description..."
                  />
                </Box>

                {/* Assignees */}
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="text.secondary"
                    textTransform="uppercase"
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={1}
                  >
                    <UserIcon size={16} /> Assignees
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {selectedTask.assigneeIds.map((id) => {
                      const user = MOCK_USERS.find((u) => u.id === id);
                      if (!user) return null;
                      return (
                        <Chip
                          key={id}
                          avatar={<Avatar src={user.avatar} />}
                          label={user.name}
                          onDelete={() => toggleTaskAssignee(user.id)}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      );
                    })}
                    <Box sx={{ position: "relative" }}>
                      <Chip
                        icon={<Plus size={14} />}
                        label="Add"
                        onClick={() => {
                          const userToAdd = MOCK_USERS.find(
                            (u) => !selectedTask.assigneeIds.includes(u.id)
                          );
                          if (userToAdd) toggleTaskAssignee(userToAdd.id);
                        }}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Box>

                <Divider />

                {/* Checklists */}
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      color="text.secondary"
                      textTransform="uppercase"
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      <CheckSquare size={16} /> Checklist
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {Math.round(
                        ((selectedTask.checklist?.filter((c) => c.isCompleted)
                          .length || 0) /
                          (selectedTask.checklist?.length || 1)) *
                          100
                      )}
                      %
                    </Typography>
                  </Box>

                  <Box display="flex" flexDirection="column" gap={1} mb={2}>
                    {(selectedTask.checklist || []).map((item) => (
                      <Box
                        key={item.id}
                        display="flex"
                        alignItems="start"
                        gap={1}
                      >
                        <IconButton
                          size="small"
                          onClick={() => toggleChecklistItem(item.id)}
                        >
                          {item.isCompleted ? (
                            <CheckSquare size={18} color={indigo[600]} />
                          ) : (
                            <Box
                              width={18}
                              height={18}
                              border={1}
                              borderRadius={0.5}
                              borderColor="grey.400"
                            />
                          )}
                        </IconButton>
                        <Box flex={1}>
                          <Typography
                            sx={{
                              textDecoration: item.isCompleted
                                ? "line-through"
                                : "none",
                              color: item.isCompleted
                                ? "text.secondary"
                                : "text.primary",
                            }}
                          >
                            {item.text}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => deleteChecklistItem(item.id)}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>

                  <Box display="flex" gap={1}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Add an item..."
                      value={newChecklistText}
                      onChange={(e) => setNewChecklistText(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleAddChecklist()
                      }
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddChecklist}
                      size="small"
                    >
                      Add
                    </Button>
                  </Box>
                </Box>

                {/* Attachments */}
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="text.secondary"
                    textTransform="uppercase"
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={2}
                  >
                    <Paperclip size={16} /> Attachments
                  </Typography>
                  <Grid container spacing={2} mb={2}>
                    {(selectedTask.attachments || []).map((att) => (
                      <Grid item xs={12} sm={6} key={att.id}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Box p={1} bgcolor="action.hover" borderRadius={1}>
                            <FileText size={20} color={grey[600]} />
                          </Box>
                          <Box overflow="hidden">
                            <Typography variant="body2" noWrap>
                              {att.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {att.size}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Upload size={16} />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ borderStyle: "dashed" }}
                  >
                    Upload File
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                  />
                </Box>

                {/* Comments */}
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    color="text.secondary"
                    textTransform="uppercase"
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mb={2}
                  >
                    <MessageSquare size={16} /> Comments (
                    {selectedTask.comments?.length || 0})
                  </Typography>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    mb={2}
                    maxHeight={300}
                    overflow="auto"
                  >
                    {(selectedTask.comments || []).map((comment) => {
                      const user = MOCK_USERS.find(
                        (u) => u.id === comment.userId
                      );
                      return (
                        <Box key={comment.id} display="flex" gap={2}>
                          <Avatar
                            src={user?.avatar}
                            sx={{ width: 32, height: 32 }}
                          />
                          <Paper
                            variant="outlined"
                            sx={{ p: 1.5, flex: 1, bgcolor: "grey.50" }}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              mb={0.5}
                            >
                              <Typography variant="subtitle2">
                                {user?.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatDistanceToNow(
                                  parseISO(comment.createdAt)
                                )}{" "}
                                ago
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {comment.text}
                            </Typography>
                          </Paper>
                        </Box>
                      );
                    })}
                  </Box>
                  <Box display="flex" gap={2}>
                    <Avatar
                      src={CURRENT_USER.avatar}
                      sx={{ width: 32, height: 32 }}
                    />
                    <Box flex={1} position="relative">
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Write a comment..."
                        value={newCommentText}
                        onChange={(e) => setNewCommentText(e.target.value)}
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={!newCommentText.trim()}
                        onClick={handleAddComment}
                        sx={{ position: "absolute", bottom: 8, right: 8 }}
                      >
                        <Send size={16} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              p={2}
              borderTop={1}
              borderColor="divider"
              bgcolor="background.paper"
            >
              <Button
                fullWidth
                variant="contained"
                onClick={() => setIsDrawerOpen(false)}
              >
                Close
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* New Task Modal */}
      <Dialog
        open={isNewTaskModalOpen}
        onClose={() => setIsNewTaskModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <form onSubmit={handleCreateTask}>
          <DialogTitle>
            <Typography
              variant="caption"
              color="primary"
              fontWeight="bold"
              display="block"
              textTransform="uppercase"
            >
              {activeProject?.name}
            </Typography>
            Create New Task
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={3} pt={1}>
              <TextField
                label="Task Title"
                name="title"
                required
                fullWidth
                placeholder="e.g. Redesign Homepage"
              />
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <TextField
                    select
                    label="Status"
                    name="status"
                    fullWidth
                    defaultValue={activeProject?.columns?.[0] || "Backlog"}
                  >
                    {(
                      activeProject?.columns || [
                        "Backlog",
                        "To Do",
                        "In Progress",
                        "Review",
                        "Done",
                      ]
                    ).map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    select
                    label="Priority"
                    name="priority"
                    fullWidth
                    defaultValue="Low"
                  >
                    {["Low", "Medium", "High", "Critical"].map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="Estimate (Hours)"
                    name="estimate"
                    type="number"
                    fullWidth
                    defaultValue={4}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              </Grid>

              <Box>
                <Typography variant="subtitle2" mb={1}>
                  Assignees
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, maxHeight: 150, overflow: "auto" }}
                >
                  <Grid container spacing={1}>
                    {MOCK_USERS.map((u) => (
                      <Grid item xs={6} key={u.id}>
                        <Box
                          component="label"
                          display="flex"
                          alignItems="center"
                          gap={1}
                          sx={{ cursor: "pointer" }}
                        >
                          <input
                            type="checkbox"
                            name="assignee"
                            value={u.id}
                            style={{ accentColor: indigo[600] }}
                          />
                          <Avatar
                            src={u.avatar}
                            sx={{ width: 24, height: 24 }}
                          />
                          <Typography variant="body2">{u.name}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Start Date"
                    name="startDate"
                    type="date"
                    required
                    fullWidth
                    defaultValue={new Date().toISOString().split("T")[0]}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Due Date"
                    name="dueDate"
                    type="date"
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <TextField
                label="Description"
                name="description"
                multiline
                rows={3}
                fullWidth
                placeholder="Brief details..."
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setIsNewTaskModalOpen(false)}
              color="inherit"
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              Create Task
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      {/* Rename Column Modal */}
      <Dialog
        open={isRenameColumnModalOpen}
        onClose={() => setIsRenameColumnModalOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Rename Status</DialogTitle>
        <DialogContent>
          <Box pt={2}>
            <TextField
              fullWidth
              label="Status Name"
              value={newColumnName}
              onChange={(e) => setNewColumnName(e.target.value)}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRenameColumnModalOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRenameColumn}
            variant="contained"
            disabled={!newColumnName.trim() || newColumnName === columnToRename}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Column Modal */}
      <Dialog
        open={isAddColumnModalOpen}
        onClose={() => setIsAddColumnModalOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <form onSubmit={handleAddColumn}>
          <DialogTitle>Add New Status</DialogTitle>
          <DialogContent>
            <Box pt={2}>
              <TextField
                fullWidth
                label="Status Name"
                value={newStatusName}
                onChange={(e) => setNewStatusName(e.target.value)}
                placeholder="e.g. In Testing"
                autoFocus
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsAddColumnModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              Add Status
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Progetti;
